import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import requestClient from '../../utils/requestClient';
import { pillarMapper } from '../../utils/pillarMapper';
import ErrorMessage from '../../components/Elements/ErrorMessage';
import ProgressBar from '../../components/Elements/ProgressBar';
import {ScaleQuestion, FreeTextQuestion} from '../../components/Elements/Questionnaire';

const Questionnaire = () => {
    const { uuid } = useParams();

    const [errors, setErrors] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    const [data, setData] = useState(null);
    const [complete, setComplete] = useState(false);
    const [questions, setQuestions] = useState({});
    const [answers, setAnswers] = useState({});

    const updateAnswers = (id, key, value) => {
        let newAnswers = { ...answers };

        newAnswers[id][key] = value

        setAnswers(newAnswers)
    }
    
    useEffect(() => {
        async function getData() {
            const response = await requestClient().get(`/api/v2/public/questionnaire/${uuid}/`)

            let answers = {}

            // Map questions to accepted response format
            response.data.data.questions.forEach(r => {
                answers[r.id] = {
                    'id': r.id,
                    'response': '',
                    'response_type': r.response_type,
                    'sentiment_free_text': null,
                    'sentiment_words': []
                }
            })

            let questions = {}

            // Map questions by pillar
            response.data.data.questions.forEach(question => {
                let key = 'other'
                
                if(question.pillar) {
                    key = question.pillar
                } else if(question.category) {
                    key = question.category.id
                }

                if (!(key in questions)) {
                    questions[key] = []
                }

                questions[key].push(question)
            })

            setAnswers(answers)
            setQuestions(questions)
            setData(response.data.data)
        }
        getData();
    }, []);

    function changeStep(event, step) {
        event.preventDefault()
        setCurrentStep(step)
    }

    async function handleSubmit(event) {
        event.preventDefault()

        setErrors(null)

        await requestClient().post(`/api/v2/public/questionnaire/${uuid}/`, { 'questions': Object.values(answers) })
            .then(response => {
                if (response.status === 201) {
                    setComplete(true)
                }
            })
            .catch(error => {
                setErrors(error.response.data.error)
            })
    }

    const progressPercentage = (index) => {
        return Math.min(((index + 1) / Object.keys(questions).length) * 100)
    }

    const handleScaleChange = (questionId, key, value) => {
        updateAnswers(questionId, key, value)
    };

    const pillar = pillarMapper(Object.keys(questions)[currentStep])

    return (data && questions ?
        <main>
            <section>
                <div className='container py-8 lg:py-12 max-w-[768px]'>
                    {pillar ? pillar.icon("max-w-[80px] mb-4") : null}
                    <div className='mb-4'>
                        <h1>{data.title}</h1>
                        {data.description && data.title !== data.description ? (<p className='mt-1 text-sm'>{data.description}</p>) : null}
                    </div>
                    <ProgressBar progress={progressPercentage(currentStep)} className="mb-6 lg:mb-12" />
                    {errors ? <ErrorMessage title="Something is not right" errors={errors} className="mb-4" /> : null}

                    {complete && (
                        <div className='mb-8'>
                            <h2 className='mb-4'>Thank you!</h2>
                            <div className='prose'>
                                <p>Thanks for taking the time to share your responses.</p>
                            </div>
                        </div>
                    )}

                    {!complete && Object.keys(questions).map((key, index) => (
                        <form key={key} className={currentStep === index ? 'block' : 'hidden'}
                            onSubmit={(event) => currentStep === Object.keys(questions).length - 1 ? handleSubmit(event) : changeStep(event, currentStep + 1)}>
                            {questions[key][0].category && (<div className='mb-4'>
                                <h3>{questions[key][0].category.title}</h3>
                            </div>)}
                            <div className='grid gap-6 lg:gap-12'>
                                {questions[key].map(question => (
                                    <div key={question.id} className='grid gap-4'>
                                        {question.response_type === 'scale' ? <ScaleQuestion question={question} answer={answers[question.id]}  value={answers[question.id].response}  onChange={(v) => handleScaleChange(question.id, 'response', v)} /> : null}
                                        {question.response_type === 'free_text' ? <FreeTextQuestion question={question} answer={answers[question.id]}  value={answers[question.id].response}  onChange={(v) => handleScaleChange(question.id, 'response', v)} /> : null}
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-between mt-6 lg:mt-12">
                                {currentStep > 0 ? (<button onClick={() => setCurrentStep(currentStep - 1)} className="btn btn-invert w-full flex justify-center lg:max-w-[250px]">Back</button>) : null}
                                <button className="btn btn-purple w-full flex justify-center lg:ml-auto lg:max-w-[250px]">{currentStep === Object.keys(questions).length - 1 ? 'FINISH' : 'NEXT'}</button>
                            </div>
                        </form>
                    ))}
                </div>
            </section>
        </main>
        : null);
};

export default Questionnaire;